'use client';
import type { ChangeEvent, CSSProperties, ReactNode } from 'react';
import { MiniMap, type Node } from '@xyflow/react';
import { Ccontainer } from '../Ccontainer';
import { ContainerPreset } from '@/constants/componentPresets';
import { GraphToolbarButton, GRAPH_TOOLBAR_SLIDER_STYLE } from './GraphToolbarButton';
/**
 * Shared graph navigation overlay — the zoom panel, 4-way arrow pad, right-side
 * controls group, and minimap that float inside `<ReactFlow>`. Extracted from
 * the mindmap so every graph widget gets identical chrome.
 *
 * Every piece of state/callback the host previously closed over is an explicit
 * prop here (zoom level + handlers, navigate handler, disabled flags, minimap
 * colors/dims). The `Ccontainer`/`ModalCard` wrapping stays internal so both
 * widgets share the exact same panel surface. The host passes its own side
 * actions (tree ops, fit toggle, …) into {@link GraphControlsOverlay}'s
 * `sideColumn` slot.
 */
/** Direction emitted by the 4-way arrow pad. */
export enum GraphNavDirection {
    Up = 'up',
    Down = 'down',
    Left = 'left',
    Right = 'right'
}
const SVG_STROKE_CURRENT = 'currentColor';
const SVG_STROKE_ROUND = 'round';
const OVERLAY_INSET = 12;
const OVERLAY_GAP = 6;
const WRAPPED_BUTTON_SIZE = 34;
const SIDE_BUTTON_SIZE = 32;
/** Vertical breathing room between the arrow pad and the minimap below it. */
const ARROW_MINIMAP_GAP = 12;
/** Minimap panel width — sized to the 3-wide arrow grid above it (so they share
 * a width and stay centered on the same axis). */
export const GRAPH_MINIMAP_WIDTH = WRAPPED_BUTTON_SIZE * 3 + OVERLAY_GAP * 2;
/** Minimap panel height — sized to the 3-tall side column beside it. */
export const GRAPH_MINIMAP_HEIGHT = SIDE_BUTTON_SIZE * 3 + OVERLAY_GAP * 2;
const RIGHT_CONTROLS_GROUP_BASE_STYLE: CSSProperties = {
    position: 'absolute',
    right: OVERLAY_INSET,
    bottom: OVERLAY_INSET,
    zIndex: 11,
    pointerEvents: 'all',
    display: 'grid',
    gridTemplateColumns: `${SIDE_BUTTON_SIZE}px ${GRAPH_MINIMAP_WIDTH}px`,
    gridTemplateRows: `${WRAPPED_BUTTON_SIZE * 2 + OVERLAY_GAP}px ${GRAPH_MINIMAP_HEIGHT}px`,
    rowGap: ARROW_MINIMAP_GAP,
    columnGap: OVERLAY_GAP,
    transformOrigin: 'bottom right',
};
const MINIMAP_WRAPPER_STYLE: CSSProperties = {
    gridColumn: 2,
    gridRow: 2,
};
const SIDE_COLUMN_STYLE: CSSProperties = {
    gridColumn: 1,
    gridRow: 2,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: OVERLAY_GAP,
};
const ARROW_GRID_STYLE: CSSProperties = {
    gridColumn: 2,
    gridRow: 1,
    display: 'grid',
    gridTemplateColumns: `repeat(3, ${WRAPPED_BUTTON_SIZE}px)`,
    gridAutoRows: `${WRAPPED_BUTTON_SIZE}px`,
    gap: OVERLAY_GAP,
    justifyContent: 'end',
    alignContent: 'end',
};
const WRAPPED_BUTTON_CONTENT_STYLE: CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};
const ZOOM_PANEL_BASE_STYLE: CSSProperties = {
    position: 'absolute',
    left: OVERLAY_INSET,
    bottom: OVERLAY_INSET,
    zIndex: 11,
    pointerEvents: 'all',
    transformOrigin: 'bottom left',
};
const ZOOM_PANEL_CONTENT_STYLE: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
};
const VERTICAL_SLIDER_BASE_STYLE: CSSProperties = {
    ...GRAPH_TOOLBAR_SLIDER_STYLE,
    width: 6,
    height: 92,
    writingMode: 'vertical-lr',
    direction: 'rtl',
};
function renderWrappedButton(button: ReactNode): ReactNode {
    return (<Ccontainer preset={ContainerPreset.ModalCard} width={WRAPPED_BUTTON_SIZE} height={WRAPPED_BUTTON_SIZE} padding={0} contentWrapperStyle={WRAPPED_BUTTON_CONTENT_STYLE}>
      {button}
    </Ccontainer>);
}
const ARROW_ICON_PATH_BY_DIRECTION: Record<GraphNavDirection, string> = {
    [GraphNavDirection.Up]: 'M3.8 8.6L7 5.4L10.2 8.6',
    [GraphNavDirection.Down]: 'M3.8 5.4L7 8.6L10.2 5.4',
    [GraphNavDirection.Left]: 'M8.6 3.8L5.4 7L8.6 10.2',
    [GraphNavDirection.Right]: 'M5.4 3.8L8.6 7L5.4 10.2',
};
const ARROW_LABEL_BY_DIRECTION: Record<GraphNavDirection, string> = {
    [GraphNavDirection.Up]: 'Navigate up',
    [GraphNavDirection.Down]: 'Navigate down',
    [GraphNavDirection.Left]: 'Navigate left',
    [GraphNavDirection.Right]: 'Navigate right',
};
function ArrowButton({ direction, disabled, onNavigate, }: {
    direction: GraphNavDirection;
    disabled: boolean;
    onNavigate: (direction: GraphNavDirection) => void;
}): ReactNode {
    return (<>
      {renderWrappedButton(<GraphToolbarButton onClick={() => onNavigate(direction)} disabled={disabled} label={ARROW_LABEL_BY_DIRECTION[direction]}>
          <svg width="20" height="20" viewBox="0 0 14 14" fill="none" aria-hidden>
            <path d={ARROW_ICON_PATH_BY_DIRECTION[direction]} stroke={SVG_STROKE_CURRENT} strokeWidth={1.2} strokeLinecap={SVG_STROKE_ROUND} strokeLinejoin={SVG_STROKE_ROUND}/>
          </svg>
        </GraphToolbarButton>)}
    </>);
}
export interface GraphArrowPanelProps {
    disabled?: boolean;
    onNavigate: (direction: GraphNavDirection) => void;
}
/** The 4-way directional pad (up/left/down/right) in its grid slot. */
export function GraphArrowPanel({ disabled = false, onNavigate, }: GraphArrowPanelProps): ReactNode {
    return (
      <div style={ARROW_GRID_STYLE}>
        <div/>
      <ArrowButton direction={GraphNavDirection.Up} disabled={disabled} onNavigate={onNavigate}/>
        <div/>
      <ArrowButton direction={GraphNavDirection.Left} disabled={disabled} onNavigate={onNavigate}/>
      <ArrowButton direction={GraphNavDirection.Down} disabled={disabled} onNavigate={onNavigate}/>
      <ArrowButton direction={GraphNavDirection.Right} disabled={disabled} onNavigate={onNavigate}/>
    </div>
    );
}
export interface GraphZoomPanelProps {
    zoomLevel: number;
    minZoom: number;
    maxZoom: number;
    disabled: boolean;
    /** Accent color for the slider thumb/track (CSS `accent-color`). */
    accentColor: string;
    /** Optional transform (e.g. mobile scale). Merged over the panel base. */
    scaleStyle?: CSSProperties;
    onZoomIn: () => void;
    onZoomOut: () => void;
    onZoomChange: (zoom: number) => void;
}
/** The bottom-left zoom panel: zoom-in, vertical slider, zoom-out. */
export function GraphZoomPanel({ zoomLevel, minZoom, maxZoom, disabled, accentColor, scaleStyle, onZoomIn, onZoomOut, onZoomChange, }: GraphZoomPanelProps): ReactNode {
    const handleSliderChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const parsedZoom = Number(event.target.value);
        if (!Number.isFinite(parsedZoom)) {
            return;
        }
        onZoomChange(Math.min(maxZoom, Math.max(minZoom, parsedZoom)));
    };
    return (
      <div style={{ ...ZOOM_PANEL_BASE_STYLE, ...scaleStyle }}>
      <Ccontainer preset={ContainerPreset.ModalCard} padding={6} contentWrapperStyle={ZOOM_PANEL_CONTENT_STYLE}>
        <GraphToolbarButton onClick={onZoomIn} disabled={disabled} label="Zoom in">
          <svg width="20" height="20" viewBox="0 0 14 14" fill="none" aria-hidden>
            <path d="M7 3.4V10.6M3.4 7H10.6" stroke={SVG_STROKE_CURRENT} strokeWidth={1.3} strokeLinecap={SVG_STROKE_ROUND}/>
          </svg>
        </GraphToolbarButton>
        <input type="range" min={minZoom} max={maxZoom} step={0.01} value={zoomLevel} onChange={handleSliderChange} aria-label="Zoom level" disabled={disabled} style={{
            ...VERTICAL_SLIDER_BASE_STYLE,
            accentColor,
            opacity: disabled ? 0.45 : 1,
        }}/>
        <GraphToolbarButton onClick={onZoomOut} disabled={disabled} label="Zoom out">
          <svg width="20" height="20" viewBox="0 0 14 14" fill="none" aria-hidden>
            <path d="M3.4 7H10.6" stroke={SVG_STROKE_CURRENT} strokeWidth={1.3} strokeLinecap={SVG_STROKE_ROUND}/>
          </svg>
        </GraphToolbarButton>
      </Ccontainer>
    </div>
    );
}
export interface GraphMiniMapPanelProps {
    width: number;
    height: number;
    nodeColor: (node: Node) => string;
    nodeStrokeColor: string;
    nodeStrokeWidth: number;
    nodeBorderRadius: number;
    maskColor: string;
    bgColor: string;
    pannable: boolean;
    zoomable: boolean;
}
const MINIMAP_INNER_CONTENT_STYLE: CSSProperties = {
    overflow: 'hidden',
    width: '100%',
    height: '100%',
};
const MINIMAP_CONTAINER_STYLE: CSSProperties = {
    position: 'relative',
};
/** The minimap, wrapped in a ModalCard, in its grid slot. */
export function GraphMiniMapPanel({ width, height, nodeColor, nodeStrokeColor, nodeStrokeWidth, nodeBorderRadius, maskColor, bgColor, pannable, zoomable, }: GraphMiniMapPanelProps): ReactNode {
    return (
      <div style={MINIMAP_WRAPPER_STYLE}>
      <Ccontainer preset={ContainerPreset.ModalCard} padding={0} width={width} height={height} style={MINIMAP_CONTAINER_STYLE} contentWrapperStyle={MINIMAP_INNER_CONTENT_STYLE}>
        <MiniMap position="top-left" nodeColor={nodeColor} nodeStrokeColor={nodeStrokeColor} nodeStrokeWidth={nodeStrokeWidth} nodeBorderRadius={nodeBorderRadius} maskColor={maskColor} bgColor={bgColor} style={{
            width,
            height,
            position: 'static',
            margin: 0,
            border: 'none',
            borderRadius: 6,
            backgroundColor: 'transparent',
            boxShadow: 'none',
            pointerEvents: 'all',
        }} pannable={pannable} zoomable={zoomable}/>
      </Ccontainer>
    </div>
    );
}
export interface GraphControlsOverlayProps {
    /** Optional transform (e.g. mobile scale). Merged over the group base. */
    scaleStyle?: CSSProperties;
    /**
     * Widget-specific side actions (mindmap tree ops, flowchart fit toggle, …).
     * Rendered into the bottom-left grid slot beside the minimap. The overlay
     * supplies the grid placement + flex column; the host supplies the buttons.
     */
    sideColumn?: ReactNode;
    /** The arrow pad + minimap panels (each self-places in the grid). */
    children: ReactNode;
}
/**
 * The bottom-right controls group: a CSS grid laying out the arrow pad (top),
 * the side column (bottom-left), and the minimap (bottom-right). Children carry
 * their own grid placement; only the `sideColumn` slot is wrapped here.
 */
export function GraphControlsOverlay({ scaleStyle, sideColumn, children, }: GraphControlsOverlayProps): ReactNode {
    return (
      <div style={{ ...RIGHT_CONTROLS_GROUP_BASE_STYLE, ...scaleStyle }}>
      {children}
      {sideColumn ? (<div style={SIDE_COLUMN_STYLE}>
          {sideColumn}
        </div>) : null}
    </div>
    );
}
